import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import {
  AdminStats,
  AdminOrderItem,
  AdminUserItem,
  AdminPayoutItem,
  AdminWithdrawalItem,
  AdminIntegration,
  AdminRaffleItem,
  AdminClientItem,
  RaffleConfig,
  RaffleWithTransactions,
} from '../api/types';
import './AdminPage.css';

type Tab = 'stats' | 'orders' | 'users' | 'payouts' | 'withdrawals' | 'integrations' | 'raffles' | 'clients';

const ORDER_STATUSES = ['', 'waiting', 'completed', 'cancelled', 'failed', 'expired'];
const PAYOUT_STATUSES = ['', 'queued', 'processing', 'sent', 'failed'];
const WITHDRAWAL_STATUSES = ['', 'pending', 'processing', 'completed', 'failed', 'cancelled'];
const RAFFLE_STATUSES = ['', 'active', 'completed', 'cancelled', 'failed', 'expired'];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUsdt(value: number | string | null): string {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return `$${num.toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: '#22c55e',
    sent: '#22c55e',
    waiting: '#f59e0b',
    pending: '#f59e0b',
    processing: '#3b82f6',
    queued: '#3b82f6',
    failed: '#ef4444',
    expired: '#6b7280',
    cancelled: '#6b7280',
  };
  const color = colors[status] || '#6b7280';
  return (
    <span className="status-badge" style={{ backgroundColor: color }}>
      {status}
    </span>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  label: string;
}) {
  return (
    <div className="filter-group">
      <label className="filter-label">{label}:</label>
      <select
        className="filter-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt || 'All'}
          </option>
        ))}
      </select>
    </div>
  );
}

function StatsPanel({ stats }: { stats: AdminStats | null }) {
  if (!stats) return <div className="admin-loading">Loading stats...</div>;

  const items = [
    { label: 'Total Orders', value: stats.totalOrders },
    { label: 'Completed', value: stats.completedOrders },
    { label: 'Today', value: stats.todayOrders },
    { label: 'Total Users', value: stats.totalUsers },
    { label: 'Volume (USDT)', value: formatUsdt(stats.totalVolumeUsdt) },
    { label: 'Pending Payouts', value: stats.pendingPayouts },
  ];

  return (
    <div className="stats-grid">
      {items.map((item) => (
        <div key={item.label} className="stat-card">
          <div className="stat-value">{item.value}</div>
          <div className="stat-label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function OrdersTable({
  orders,
  loading,
  statusFilter,
  onStatusChange,
  onConfirmOrder,
}: {
  orders: AdminOrderItem[];
  loading: boolean;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  onConfirmOrder: (order: AdminOrderItem) => void;
}) {
  return (
    <>
      <div className="filters-row">
        <FilterSelect
          value={statusFilter}
          onChange={onStatusChange}
          options={ORDER_STATUSES}
          label="Status"
        />
      </div>
      {loading ? (
        <div className="admin-loading">Loading orders...</div>
      ) : !orders.length ? (
        <div className="admin-empty">No orders found</div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>User</th>
                <th>Amount</th>
                <th>Net</th>
                <th>Fee</th>
                <th>Status</th>
                <th>Integration</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="mono">{order.orderId}</td>
                  <td>
                    {order.user ? (
                      <>
                        {order.user.username || 'N/A'}
                        <span className="text-muted"> #{order.user.telegramId}</span>
                      </>
                    ) : (
                      <span className="text-muted">Anonymous</span>
                    )}
                  </td>
                  <td>{formatUsdt(order.usdtAmount)}</td>
                  <td>{formatUsdt(order.netAmountUsdt)}</td>
                  <td>{formatUsdt(order.serviceFeeUsdt)}</td>
                  <td>
                    <StatusBadge status={order.status} />
                  </td>
                  <td>{order.integrationCode}</td>
                  <td>{formatDate(order.createdAt)}</td>
                  <td className="actions-cell">
                    {order.status !== 'completed' && (
                      <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => onConfirmOrder(order)}
                        title="Manually confirm payment"
                      >
                        Confirm
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface BalanceCorrectionModalProps {
  user: AdminUserItem | null;
  onClose: () => void;
  onSubmit: (userId: string, amount: number, reason: string) => Promise<void>;
}

interface OrderConfirmModalProps {
  order: AdminOrderItem | null;
  onClose: () => void;
  onSubmit: (orderId: string, reason: string) => Promise<void>;
}

function OrderConfirmModal({ order, onClose, onSubmit }: OrderConfirmModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!order) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(order.orderId, reason.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Manual Order Confirmation</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="modal-user-info">
              <strong>{order.orderId}</strong>
              <br />
              <span className="text-muted">
                Amount: {formatUsdt(order.netAmountUsdt)} | Status: {order.status}
              </span>
            </div>

            {error && <div className="modal-error">{error}</div>}

            <div className="form-group">
              <label>Reason for manual confirmation</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Payment received on OxaPay but callback failed"
                className="form-textarea"
                rows={3}
                disabled={submitting}
              />
              <small className="form-hint">
                This will mark the order as completed and credit the user's balance
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Confirming...' : 'Confirm Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BalanceCorrectionModal({ user, onClose, onSubmit }: BalanceCorrectionModalProps) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount === 0) {
      setError('Amount must be a non-zero number');
      return;
    }

    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(user.id, numAmount, reason.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to correct balance');
    } finally {
      setSubmitting(false);
    }
  };

  const currentBalance = Number(user.userBalance?.balanceUsdt ?? 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Balance Correction</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="modal-user-info">
              <strong>{user.username || user.telegramId}</strong>
              <span className="text-muted"> (Current: {formatUsdt(currentBalance)})</span>
            </div>

            {error && <div className="modal-error">{error}</div>}

            <div className="form-group">
              <label>Amount (USDT)</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 10.00 or -5.00"
                className="form-input"
                disabled={submitting}
              />
              <small className="form-hint">
                Positive to add, negative to subtract
              </small>
            </div>

            <div className="form-group">
              <label>Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for correction..."
                className="form-textarea"
                rows={3}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Apply Correction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UsersTable({
  users,
  loading,
  onToggleAdmin,
  onToggleFeeExempt,
  onCorrectBalance,
  searchQuery,
  onSearchChange,
}: {
  users: AdminUserItem[];
  loading: boolean;
  onToggleAdmin: (userId: string, isAdmin: boolean) => void;
  onToggleFeeExempt: (userId: string, exempt: boolean) => void;
  onCorrectBalance: (user: AdminUserItem) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}) {
  const filteredUsers = users.filter((u) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      u.telegramId.toLowerCase().includes(q) ||
      (u.username?.toLowerCase().includes(q) ?? false)
    );
  });

  return (
    <>
      <div className="filters-row">
        <div className="filter-group">
          <label className="filter-label">Search:</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Telegram ID or username"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      {loading ? (
        <div className="admin-loading">Loading users...</div>
      ) : !filteredUsers.length ? (
        <div className="admin-empty">No users found</div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Telegram ID</th>
                <th>Username</th>
                <th>Balance</th>
                <th>Orders</th>
                <th>Admin</th>
                <th>Fee Exempt</th>
                <th>Merchant</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="mono">{user.telegramId}</td>
                  <td>{user.username || <span className="text-muted">N/A</span>}</td>
                  <td>{formatUsdt(user.userBalance?.balanceUsdt ?? 0)}</td>
                  <td>{user._count.orders}</td>
                  <td>{user.isAdmin ? 'Yes' : 'No'}</td>
                  <td>{user.isServiceFeeExempt ? 'Yes' : 'No'}</td>
                  <td>{user.isPotentialMerchant ? 'Yes' : 'No'}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td className="actions-cell">
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => onCorrectBalance(user)}
                      title="Correct balance"
                    >
                      Balance
                    </button>
                    <button
                      className={`admin-btn admin-btn-small ${user.isServiceFeeExempt ? 'admin-btn-success' : 'admin-btn-secondary'}`}
                      onClick={() => onToggleFeeExempt(user.id, !user.isServiceFeeExempt)}
                      title={user.isServiceFeeExempt ? 'Remove fee exemption' : 'Exempt from service fee'}
                    >
                      {user.isServiceFeeExempt ? 'VIP' : 'No Fee'}
                    </button>
                    <button
                      className={`admin-btn ${user.isAdmin ? 'admin-btn-danger' : 'admin-btn-primary'}`}
                      onClick={() => onToggleAdmin(user.id, !user.isAdmin)}
                    >
                      {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function PayoutsTable({
  payouts,
  loading,
  statusFilter,
  onStatusChange,
  onRetry,
  retryingId,
}: {
  payouts: AdminPayoutItem[];
  loading: boolean;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  onRetry: (payoutId: string) => void;
  retryingId: string | null;
}) {
  return (
    <>
      <div className="filters-row">
        <FilterSelect
          value={statusFilter}
          onChange={onStatusChange}
          options={PAYOUT_STATUSES}
          label="Status"
        />
      </div>
      {loading ? (
        <div className="admin-loading">Loading payouts...</div>
      ) : !payouts.length ? (
        <div className="admin-empty">No payouts found</div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Address</th>
                <th>Amount</th>
                <th>Status</th>
                <th>TX ID</th>
                <th>Error</th>
                <th>Created</th>
                <th>Sent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => (
                <tr key={payout.id}>
                  <td className="mono">{payout.orderId}</td>
                  <td className="mono truncate" title={payout.merchantAddress}>
                    {payout.merchantAddress}
                  </td>
                  <td>{formatUsdt(payout.amountUsdt)}</td>
                  <td>
                    <StatusBadge status={payout.status} />
                  </td>
                  <td className="mono truncate" title={payout.txId || ''}>
                    {payout.txId || '-'}
                  </td>
                  <td className="text-error truncate" title={payout.lastError || ''}>
                    {payout.lastError || '-'}
                  </td>
                  <td>{formatDate(payout.createdAt)}</td>
                  <td>{formatDate(payout.sentAt)}</td>
                  <td>
                    {payout.status === 'failed' && (
                      <button
                        className="admin-btn admin-btn-small admin-btn-primary"
                        onClick={() => onRetry(payout.id)}
                        disabled={retryingId === payout.id}
                      >
                        {retryingId === payout.id ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function WithdrawalsTable({
  withdrawals,
  loading,
  statusFilter,
  onStatusChange,
  onRetry,
  retryingId,
  onComplete,
  completingId,
}: {
  withdrawals: AdminWithdrawalItem[];
  loading: boolean;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  onRetry: (id: string) => void;
  retryingId: string | null;
  onComplete: (id: string) => void;
  completingId: string | null;
}) {
  return (
    <>
      <div className="filters-row">
        <FilterSelect
          value={statusFilter}
          onChange={onStatusChange}
          options={WITHDRAWAL_STATUSES}
          label="Status"
        />
      </div>
      {loading ? (
        <div className="admin-loading">Loading withdrawals...</div>
      ) : !withdrawals.length ? (
        <div className="admin-empty">No withdrawals found</div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Address</th>
                <th>Amount</th>
                <th>Status</th>
                <th>TX ID</th>
                <th>Error</th>
                <th>Created</th>
                <th>Processed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id}>
                  <td>
                    {w.balance.user.username || 'N/A'}
                    <span className="text-muted"> #{w.balance.user.telegramId}</span>
                  </td>
                  <td className="mono truncate" title={w.withdrawalAddress}>
                    {w.withdrawalAddress}
                  </td>
                  <td>{formatUsdt(w.amountUsdt)}</td>
                  <td>
                    <StatusBadge status={w.status} />
                  </td>
                  <td className="mono truncate" title={w.txId || ''}>
                    {w.txId || '-'}
                  </td>
                  <td className="text-error truncate" title={w.errorMessage || ''}>
                    {w.errorMessage || '-'}
                  </td>
                  <td>{formatDate(w.createdAt)}</td>
                  <td>{formatDate(w.processedAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {w.status === 'failed' && (
                      <button
                        className="admin-btn admin-btn-small admin-btn-primary"
                        onClick={() => onRetry(w.id)}
                        disabled={retryingId === w.id}
                      >
                        {retryingId === w.id ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                    {(w.status === 'processing' || w.status === 'failed') && (
                      <button
                        className="admin-btn admin-btn-small admin-btn-success"
                        style={{ marginLeft: w.status === 'failed' ? '6px' : '0' }}
                        onClick={() => onComplete(w.id)}
                        disabled={completingId === w.id}
                      >
                        {completingId === w.id ? '...' : '✓ Done'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface IntegrationEditModalProps {
  integration: AdminIntegration | null;
  onClose: () => void;
  onSubmit: (code: string, data: { feePercent: number; flatFeeUsdt: number }) => Promise<void>;
}

function IntegrationEditModal({ integration, onClose, onSubmit }: IntegrationEditModalProps) {
  const [feePercent, setFeePercent] = useState('');
  const [flatFeeUsdt, setFlatFeeUsdt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (integration) {
      setFeePercent(Number(integration.feePercent).toString());
      setFlatFeeUsdt(Number(integration.flatFeeUsdt).toString());
    }
  }, [integration]);

  if (!integration) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const feePercentNum = parseFloat(feePercent);
    const flatFeeNum = parseFloat(flatFeeUsdt);

    if (isNaN(feePercentNum) || feePercentNum < 0) {
      setError('Fee percent must be a non-negative number');
      return;
    }

    if (isNaN(flatFeeNum) || flatFeeNum < 0) {
      setError('Flat fee must be a non-negative number');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(integration.code, { feePercent: feePercentNum, flatFeeUsdt: flatFeeNum });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update integration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Fees: {integration.name}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            <div className="form-group">
              <label>Service Fee Percent (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={feePercent}
                onChange={(e) => setFeePercent(e.target.value)}
                placeholder="e.g. 0.5"
                className="form-input"
                disabled={submitting}
              />
              <small className="form-hint">
                Percentage of transaction amount (0.5 = 0.5%)
              </small>
            </div>

            <div className="form-group">
              <label>Flat Fee (USDT)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={flatFeeUsdt}
                onChange={(e) => setFlatFeeUsdt(e.target.value)}
                placeholder="e.g. 0.50"
                className="form-input"
                disabled={submitting}
              />
              <small className="form-hint">
                Fixed fee per transaction in USDT
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : 'Save Fees'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RaffleDetailsModal({
  raffle,
  onClose,
}: {
  raffle: RaffleWithTransactions | null;
  onClose: () => void;
}) {
  if (!raffle) return null;

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      raffle_create: '📤 Created (debit)',
      raffle_win: '📥 Prize (credit)',
      raffle_refund: '↩️ Refund (credit)',
    };
    return labels[type] || type;
  };

  const getTransactionColor = (type: string) => {
    if (type === 'raffle_create') return '#ef4444';
    if (type === 'raffle_win' || type === 'raffle_refund') return '#22c55e';
    return '#6b7280';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📋 Raffle Details: {raffle.raffleId}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Raffle Info */}
          <div className="raffle-details-section">
            <h4>Info</h4>
            <div className="raffle-details-grid">
              <div>
                <span className="label">Prize:</span>
                <span className="value">{formatUsdt(raffle.prizeAmountUsdt)}</span>
              </div>
              <div>
                <span className="label">Service Fee:</span>
                <span className="value">{formatUsdt(raffle.serviceFeeUsdt)}</span>
              </div>
              <div>
                <span className="label">Total Frozen:</span>
                <span className="value">{formatUsdt(raffle.totalFrozenUsdt)}</span>
              </div>
              <div>
                <span className="label">Status:</span>
                <StatusBadge status={raffle.status} />
              </div>
              <div>
                <span className="label">Creator:</span>
                <span className="value">
                  {raffle.creator?.username ? `@${raffle.creator.username}` : raffle.creator?.telegramId || 'N/A'}
                </span>
              </div>
              {raffle.winner && (
                <div>
                  <span className="label">Winner:</span>
                  <span className="value">
                    {raffle.winner.username ? `@${raffle.winner.username}` : raffle.winner.telegramId}
                  </span>
                </div>
              )}
              <div>
                <span className="label">Scheduled:</span>
                <span className="value">{formatDate(raffle.scheduledAt)}</span>
              </div>
              {raffle.completedAt && (
                <div>
                  <span className="label">Completed:</span>
                  <span className="value">{formatDate(raffle.completedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Transactions */}
          <div className="raffle-details-section">
            <h4>💰 Balance Transactions</h4>
            {raffle.balanceTransactions.length === 0 ? (
              <p className="text-muted">No transactions found</p>
            ) : (
              <table className="admin-table raffle-transactions-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {raffle.balanceTransactions.map((tx) => (
                    <tr key={tx.id}>
                      <td>{getTransactionTypeLabel(tx.type)}</td>
                      <td>
                        {tx.balance.user.username
                          ? `@${tx.balance.user.username}`
                          : `#${tx.balance.user.telegramId}`}
                      </td>
                      <td style={{ color: getTransactionColor(tx.type), fontWeight: 'bold' }}>
                        {Number(tx.amount) >= 0 ? '+' : ''}{formatUsdt(tx.amount)}
                      </td>
                      <td className="text-muted">{tx.description || '-'}</td>
                      <td>{formatDate(tx.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function RafflesTable({
  raffles,
  loading,
  statusFilter,
  onStatusChange,
  onViewDetails,
  onResendNotification,
  onUpdateChatMessage,
  notifyingId,
  updatingMessageId,
}: {
  raffles: AdminRaffleItem[];
  loading: boolean;
  statusFilter: string;
  onStatusChange: (status: string) => void;
  onViewDetails: (raffleId: string) => void;
  onResendNotification: (raffleId: string) => void;
  onUpdateChatMessage: (raffleId: string) => void;
  notifyingId: string | null;
  updatingMessageId: string | null;
}) {
  return (
    <>
      <div className="filters-row">
        <FilterSelect
          value={statusFilter}
          onChange={onStatusChange}
          options={RAFFLE_STATUSES}
          label="Status"
        />
      </div>
      {loading ? (
        <div className="admin-loading">Loading raffles...</div>
      ) : !raffles.length ? (
        <div className="admin-empty">No raffles found</div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table raffle-table-clickable">
            <thead>
              <tr>
                <th>Raffle ID</th>
                <th>Creator</th>
                <th>Prize</th>
                <th>Participants</th>
                <th>Winner</th>
                <th>Status</th>
                <th>Payout</th>
                <th>Scheduled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {raffles.map((raffle) => (
                <tr
                  key={raffle.id}
                  className="clickable-row"
                  onClick={() => onViewDetails(raffle.id)}
                >
                  <td className="mono">{raffle.raffleId}</td>
                  <td>
                    {raffle.creator?.username || 'N/A'}
                    <span className="text-muted"> #{raffle.creator?.telegramId}</span>
                  </td>
                  <td>{formatUsdt(raffle.prizeAmountUsdt)}</td>
                  <td>{raffle._count.participants}</td>
                  <td>
                    {raffle.winnerUsername ? (
                      <>@{raffle.winnerUsername}</>
                    ) : raffle.winnerTelegramId ? (
                      <span className="text-muted">#{raffle.winnerTelegramId}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <StatusBadge status={raffle.status} />
                  </td>
                  <td>
                    {raffle.payoutStatus ? (
                      <StatusBadge status={raffle.payoutStatus} />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{formatDate(raffle.scheduledAt)}</td>
                  <td className="actions-cell raffle-actions" onClick={(e) => e.stopPropagation()}>
                    {raffle.status === 'completed' && raffle.winnerTelegramId && (
                      <>
                        <button
                          className="admin-btn admin-btn-small admin-btn-secondary"
                          onClick={() => onResendNotification(raffle.id)}
                          disabled={notifyingId === raffle.id}
                          title="Resend winner notification"
                        >
                          {notifyingId === raffle.id ? '...' : 'Notify'}
                        </button>
                        <button
                          className="admin-btn admin-btn-small admin-btn-secondary"
                          onClick={() => onUpdateChatMessage(raffle.id)}
                          disabled={updatingMessageId === raffle.id}
                          title="Update chat message with winner"
                        >
                          {updatingMessageId === raffle.id ? '...' : 'Update Msg'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RaffleConfigPanel({
  config,
  loading,
  onSave,
}: {
  config: RaffleConfig | null;
  loading: boolean;
  onSave: (data: Partial<RaffleConfig>) => Promise<void>;
}) {
  const [localConfig, setLocalConfig] = useState<Partial<RaffleConfig>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setLocalConfig({
        serviceFeePercent: config.serviceFeePercent,
        transferFeeTrx: config.transferFeeTrx,
        trxToUsdtRate: config.trxToUsdtRate,
        minPrizeUsdt: config.minPrizeUsdt,
        maxPrizeUsdt: config.maxPrizeUsdt,
        enabled: config.enabled,
      });
    }
  }, [config]);

  if (loading || !config) {
    return <div className="admin-loading">Loading config...</div>;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localConfig);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="raffle-config-panel">
      <h3>Raffle Settings</h3>
      <div className="config-grid">
        <div className="form-group">
          <label>Service Fee (%)</label>
          <input
            type="number"
            step="0.01"
            value={localConfig.serviceFeePercent ?? ''}
            onChange={(e) => setLocalConfig({ ...localConfig, serviceFeePercent: parseFloat(e.target.value) })}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>Network Fee (TRX)</label>
          <input
            type="number"
            step="0.01"
            value={localConfig.transferFeeTrx ?? ''}
            onChange={(e) => setLocalConfig({ ...localConfig, transferFeeTrx: parseFloat(e.target.value) })}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>TRX/USDT Rate</label>
          <input
            type="number"
            step="0.0001"
            value={localConfig.trxToUsdtRate ?? ''}
            onChange={(e) => setLocalConfig({ ...localConfig, trxToUsdtRate: parseFloat(e.target.value) })}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>Min Prize (USDT)</label>
          <input
            type="number"
            step="1"
            value={localConfig.minPrizeUsdt ?? ''}
            onChange={(e) => setLocalConfig({ ...localConfig, minPrizeUsdt: parseFloat(e.target.value) })}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>Max Prize (USDT)</label>
          <input
            type="number"
            step="1"
            value={localConfig.maxPrizeUsdt ?? ''}
            onChange={(e) => setLocalConfig({ ...localConfig, maxPrizeUsdt: parseFloat(e.target.value) })}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={localConfig.enabled ?? false}
              onChange={(e) => setLocalConfig({ ...localConfig, enabled: e.target.checked })}
            />
            {' '}Raffles Enabled
          </label>
        </div>
      </div>
      <button
        className="admin-btn admin-btn-primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}

function IntegrationsTable({
  integrations,
  loading,
  onToggleActive,
  onEditFees,
}: {
  integrations: AdminIntegration[];
  loading: boolean;
  onToggleActive: (code: string, isActive: boolean) => void;
  onEditFees: (integration: AdminIntegration) => void;
}) {
  if (loading) return <div className="admin-loading">Loading integrations...</div>;
  if (!integrations.length) return <div className="admin-empty">No integrations found</div>;

  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Fee %</th>
            <th>Flat Fee</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {integrations.map((intg) => (
            <tr key={intg.id}>
              <td className="mono">{intg.code}</td>
              <td>{intg.name}</td>
              <td>{Number(intg.feePercent).toFixed(2)}%</td>
              <td>{formatUsdt(intg.flatFeeUsdt)}</td>
              <td>{intg.isActive ? 'Yes' : 'No'}</td>
              <td className="actions-cell">
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => onEditFees(intg)}
                  title="Edit fee settings"
                >
                  Edit Fees
                </button>
                <button
                  className={`admin-btn ${intg.isActive ? 'admin-btn-danger' : 'admin-btn-primary'}`}
                  onClick={() => onToggleActive(intg.code, !intg.isActive)}
                >
                  {intg.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientsTable({
  clients,
  loading,
  onUpdate,
}: {
  clients: AdminClientItem[];
  loading: boolean;
  onUpdate: (userId: string, data: Partial<AdminClientItem>) => Promise<void>;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ companyName: string; companyUrl: string; companyLogo: string }>({
    companyName: '',
    companyUrl: '',
    companyLogo: '',
  });
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const startEdit = (client: AdminClientItem) => {
    setEditingId(client.id);
    setEditForm({
      companyName: client.companyName || '',
      companyUrl: client.companyUrl || '',
      companyLogo: client.companyLogo || '',
    });
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (client: AdminClientItem) => {
    setSaving(true);
    try {
      await onUpdate(client.id, {
        companyName: editForm.companyName,
        companyUrl: editForm.companyUrl,
        companyLogo: editForm.companyLogo,
      });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const toggleHomepage = async (client: AdminClientItem) => {
    await onUpdate(client.id, { showOnHomepage: !client.showOnHomepage });
  };

  if (loading) return <div className="admin-loading">Loading clients...</div>;
  if (!clients.length)
    return (
      <div className="admin-empty">
        No merchants yet. Users with merchant API tokens will appear here.
      </div>
    );

  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Company Name</th>
            <th>Website</th>
            <th>Logo URL</th>
            <th>Tokens</th>
            <th>Show on site</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr key={client.id} className={client.showOnHomepage ? 'clients-row-active' : ''}>
              <td>
                {client.username ? `@${client.username}` : 'N/A'}
                <span className="text-muted"> #{client.telegramId}</span>
              </td>
              {editingId === client.id ? (
                <>
                  <td>
                    <input
                      ref={nameRef}
                      className="admin-inline-input"
                      value={editForm.companyName}
                      onChange={(e) => setEditForm((f) => ({ ...f, companyName: e.target.value }))}
                      placeholder="Company name"
                    />
                  </td>
                  <td>
                    <input
                      className="admin-inline-input"
                      value={editForm.companyUrl}
                      onChange={(e) => setEditForm((f) => ({ ...f, companyUrl: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </td>
                  <td>
                    <input
                      className="admin-inline-input"
                      value={editForm.companyLogo}
                      onChange={(e) => setEditForm((f) => ({ ...f, companyLogo: e.target.value }))}
                      placeholder="https://... logo URL"
                    />
                  </td>
                </>
              ) : (
                <>
                  <td>{client.companyName || <span className="text-muted">—</span>}</td>
                  <td>
                    {client.companyUrl ? (
                      <a href={client.companyUrl} target="_blank" rel="noopener noreferrer" className="admin-link">
                        {client.companyUrl.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {client.companyLogo ? (
                      <div className="clients-logo-preview">
                        <img src={client.companyLogo} alt="logo" className="clients-logo-img" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </>
              )}
              <td>{client._count.merchantApiTokens}</td>
              <td>
                <label className="clients-toggle">
                  <input
                    type="checkbox"
                    checked={client.showOnHomepage}
                    onChange={() => toggleHomepage(client)}
                  />
                  <span className="clients-toggle-track" />
                </label>
              </td>
              <td>
                {editingId === client.id ? (
                  <div className="admin-actions">
                    <button className="admin-btn admin-btn-sm admin-btn-primary" onClick={() => saveEdit(client)} disabled={saving}>
                      {saving ? '…' : 'Save'}
                    </button>
                    <button className="admin-btn admin-btn-sm admin-btn-secondary" onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="admin-btn admin-btn-sm admin-btn-secondary" onClick={() => startEdit(client)}>
                    Edit
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('stats');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [orders, setOrders] = useState<AdminOrderItem[]>([]);
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [payouts, setPayouts] = useState<AdminPayoutItem[]>([]);
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawalItem[]>([]);
  const [integrations, setIntegrations] = useState<AdminIntegration[]>([]);
  const [raffles, setRaffles] = useState<AdminRaffleItem[]>([]);
  const [clients, setClients] = useState<AdminClientItem[]>([]);
  const [raffleConfig, setRaffleConfig] = useState<RaffleConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [orderStatus, setOrderStatus] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('');
  const [withdrawalStatus, setWithdrawalStatus] = useState('');
  const [raffleStatus, setRaffleStatus] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Balance correction modal
  const [correctionUser, setCorrectionUser] = useState<AdminUserItem | null>(null);

  // Order confirmation modal
  const [confirmOrder, setConfirmOrder] = useState<AdminOrderItem | null>(null);

  // Integration edit modal
  const [editIntegration, setEditIntegration] = useState<AdminIntegration | null>(null);

  // Payout retry state
  const [retryingPayoutId, setRetryingPayoutId] = useState<string | null>(null);
  const [retryingWithdrawalId, setRetryingWithdrawalId] = useState<string | null>(null);
  const [completingWithdrawalId, setCompletingWithdrawalId] = useState<string | null>(null);

  // Raffle action states
  const [notifyingRaffleId, setNotifyingRaffleId] = useState<string | null>(null);
  const [updatingRaffleMessageId, setUpdatingRaffleMessageId] = useState<string | null>(null);
  const [selectedRaffleDetails, setSelectedRaffleDetails] = useState<RaffleWithTransactions | null>(null);
  const [loadingRaffleDetails, setLoadingRaffleDetails] = useState(false);

  const loadData = useCallback(async (currentTab: Tab, filters?: Record<string, string>) => {
    setLoading(true);
    setError(null);
    try {
      switch (currentTab) {
        case 'stats':
          setStats(await api.getAdminStats());
          break;
        case 'orders':
          setOrders((await api.getAdminOrders(100, 0, filters?.status)).items);
          break;
        case 'users':
          setUsers((await api.getAdminUsers(100)).items);
          break;
        case 'payouts':
          setPayouts((await api.getAdminPayouts(100, 0, filters?.status)).items);
          break;
        case 'withdrawals':
          setWithdrawals((await api.getAdminWithdrawals(100, 0, filters?.status)).items);
          break;
        case 'integrations':
          setIntegrations(await api.getAdminIntegrations());
          break;
        case 'raffles':
          setRaffles(await api.getAdminRaffles(100, 0, filters?.status));
          setRaffleConfig(await api.getRaffleConfig());
          break;
        case 'clients':
          setClients(await api.getAdminClients());
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const filters: Record<string, string> = {};
    if (tab === 'orders' && orderStatus) filters.status = orderStatus;
    if (tab === 'payouts' && payoutStatus) filters.status = payoutStatus;
    if (tab === 'withdrawals' && withdrawalStatus) filters.status = withdrawalStatus;
    if (tab === 'raffles' && raffleStatus) filters.status = raffleStatus;
    void loadData(tab, filters);
  }, [tab, orderStatus, payoutStatus, withdrawalStatus, raffleStatus, loadData]);

  const handleToggleAdmin = async (userId: string, isAdmin: boolean) => {
    try {
      await api.toggleUserAdmin(userId, isAdmin);
      await loadData('users');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleToggleFeeExempt = async (userId: string, exempt: boolean) => {
    try {
      await api.toggleServiceFeeExempt(userId, exempt);
      await loadData('users');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update fee exemption');
    }
  };

  const handleToggleIntegration = async (code: string, isActive: boolean) => {
    try {
      await api.updateAdminIntegration(code, { isActive });
      await loadData('integrations');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update integration');
    }
  };

  const handleEditIntegrationFees = async (code: string, data: { feePercent: number; flatFeeUsdt: number }) => {
    await api.updateAdminIntegration(code, data);
    await loadData('integrations');
  };

  const handleCorrectBalance = async (userId: string, amount: number, reason: string) => {
    await api.correctUserBalance(userId, amount, reason);
    await loadData('users');
  };

  const handleConfirmOrder = async (orderId: string, reason: string) => {
    await api.confirmOrderManually(orderId, reason);
    await loadData('orders', { status: orderStatus });
  };

  const handleRetryPayout = async (payoutId: string) => {
    setRetryingPayoutId(payoutId);
    try {
      await api.retryMerchantPayout(payoutId);
      await loadData('payouts', { status: payoutStatus });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry payout');
    } finally {
      setRetryingPayoutId(null);
    }
  };

  const handleRetryWithdrawal = async (withdrawalId: string) => {
    setRetryingWithdrawalId(withdrawalId);
    try {
      await api.retryAdminWithdrawal(withdrawalId);
      await loadData('withdrawals', { status: withdrawalStatus });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry withdrawal');
    } finally {
      setRetryingWithdrawalId(null);
    }
  };

  const handleCompleteWithdrawal = async (withdrawalId: string) => {
    setCompletingWithdrawalId(withdrawalId);
    try {
      await api.completeAdminWithdrawal(withdrawalId);
      await loadData('withdrawals', { status: withdrawalStatus });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete withdrawal');
    } finally {
      setCompletingWithdrawalId(null);
    }
  };

  const handleViewRaffleDetails = async (raffleId: string) => {
    setLoadingRaffleDetails(true);
    try {
      const details = await api.getRaffleWithTransactions(raffleId);
      setSelectedRaffleDetails(details);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load raffle details');
    } finally {
      setLoadingRaffleDetails(false);
    }
  };

  const handleResendNotification = async (raffleId: string) => {
    setNotifyingRaffleId(raffleId);
    try {
      const result = await api.resendWinnerNotification(raffleId);
      if (!result.success) {
        setError(result.error || 'Failed to send notification');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setNotifyingRaffleId(null);
    }
  };

  const handleUpdateChatMessage = async (raffleId: string) => {
    setUpdatingRaffleMessageId(raffleId);
    try {
      const result = await api.updateRaffleChatMessage(raffleId);
      if (!result.success) {
        setError(result.error || 'Failed to update message');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update message');
    } finally {
      setUpdatingRaffleMessageId(null);
    }
  };

  const handleSaveRaffleConfig = async (data: Partial<RaffleConfig>) => {
    await api.updateRaffleConfig(data);
    setRaffleConfig(await api.getRaffleConfig());
  };

  const handleUpdateClient = async (userId: string, data: Partial<AdminClientItem>) => {
    await api.updateAdminClient(userId, data);
    setClients(await api.getAdminClients());
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'stats', label: 'Dashboard' },
    { key: 'orders', label: 'Orders' },
    { key: 'users', label: 'Users' },
    { key: 'payouts', label: 'Payouts' },
    { key: 'withdrawals', label: 'Withdrawals' },
    { key: 'integrations', label: 'Integrations' },
    { key: 'raffles', label: 'Raffles' },
    { key: 'clients', label: 'Clients' },
  ];

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <button
          className="admin-btn admin-btn-secondary"
          onClick={() => {
            const filters: Record<string, string> = {};
            if (tab === 'orders' && orderStatus) filters.status = orderStatus;
            if (tab === 'payouts' && payoutStatus) filters.status = payoutStatus;
            if (tab === 'withdrawals' && withdrawalStatus) filters.status = withdrawalStatus;
            if (tab === 'raffles' && raffleStatus) filters.status = raffleStatus;
            void loadData(tab, filters);
          }}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      <nav className="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`admin-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <button
          className="admin-tab admin-tab--link"
          onClick={() => navigate('/admin/merchants')}
          title="Merchant Fee Management"
        >
          Merchants
        </button>
      </nav>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-content">
        {tab === 'stats' && <StatsPanel stats={stats} />}
        {tab === 'orders' && (
          <OrdersTable
            orders={orders}
            loading={loading}
            statusFilter={orderStatus}
            onStatusChange={setOrderStatus}
            onConfirmOrder={setConfirmOrder}
          />
        )}
        {tab === 'users' && (
          <UsersTable
            users={users}
            loading={loading}
            onToggleAdmin={handleToggleAdmin}
            onToggleFeeExempt={handleToggleFeeExempt}
            onCorrectBalance={setCorrectionUser}
            searchQuery={userSearch}
            onSearchChange={setUserSearch}
          />
        )}
        {tab === 'payouts' && (
          <PayoutsTable
            payouts={payouts}
            loading={loading}
            statusFilter={payoutStatus}
            onStatusChange={setPayoutStatus}
            onRetry={handleRetryPayout}
            retryingId={retryingPayoutId}
          />
        )}
        {tab === 'withdrawals' && (
          <WithdrawalsTable
            withdrawals={withdrawals}
            loading={loading}
            statusFilter={withdrawalStatus}
            onStatusChange={setWithdrawalStatus}
            onRetry={handleRetryWithdrawal}
            retryingId={retryingWithdrawalId}
            onComplete={handleCompleteWithdrawal}
            completingId={completingWithdrawalId}
          />
        )}
        {tab === 'integrations' && (
          <IntegrationsTable
            integrations={integrations}
            loading={loading}
            onToggleActive={handleToggleIntegration}
            onEditFees={setEditIntegration}
          />
        )}
        {tab === 'raffles' && (
          <>
            <RaffleConfigPanel
              config={raffleConfig}
              loading={loading}
              onSave={handleSaveRaffleConfig}
            />
            <RafflesTable
              raffles={raffles}
              loading={loading}
              statusFilter={raffleStatus}
              onStatusChange={setRaffleStatus}
              onViewDetails={handleViewRaffleDetails}
              onResendNotification={handleResendNotification}
              onUpdateChatMessage={handleUpdateChatMessage}
              notifyingId={notifyingRaffleId}
              updatingMessageId={updatingRaffleMessageId}
            />
          </>
        )}
        {tab === 'clients' && (
          <ClientsTable
            clients={clients}
            loading={loading}
            onUpdate={handleUpdateClient}
          />
        )}
      </div>

      {/* Balance Correction Modal */}
      <BalanceCorrectionModal
        user={correctionUser}
        onClose={() => setCorrectionUser(null)}
        onSubmit={handleCorrectBalance}
      />

      {/* Order Confirmation Modal */}
      <OrderConfirmModal
        order={confirmOrder}
        onClose={() => setConfirmOrder(null)}
        onSubmit={handleConfirmOrder}
      />

      {/* Integration Edit Modal */}
      <IntegrationEditModal
        integration={editIntegration}
        onClose={() => setEditIntegration(null)}
        onSubmit={handleEditIntegrationFees}
      />

      {/* Raffle Details Modal */}
      <RaffleDetailsModal
        raffle={selectedRaffleDetails}
        onClose={() => setSelectedRaffleDetails(null)}
      />
    </div>
  );
}
